import { useQuery } from '@tanstack/react-query';
import {
  Link,
  createFileRoute,
  notFound,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';

import { LabelMapSchema } from '@/api';
import { useLabel, useLabelMapId } from '@/api/queries/label';
import { Button } from '@/components/Button/Button';
import { Explorer } from '@/components/Explorer';
import { ModalTrigger } from '@/components/Modal';
import { PaginationLinks } from '@/components/PaginationLinks/PaginationLinks';
import { TextButton } from '@/components/TextButton/TextButton';
import { RegularChevronRight } from '@/icons/components';
import { Label } from '@/modals/Label';

import { ExplorerButtonLabel } from './-components/ExplorerButtonLabel';
import { SidebarLayout } from './-components/SidebarLayout';

const searchSchema = z.object({
  query: z.string().optional(),
  p: z.number().optional().default(1),
});

type SearchParams = z.infer<typeof searchSchema>;

/**
 * Query options
 */
const labelMapIdQueryOptions = (nestedMapId: string) =>
  useLabelMapId.getOptions(nestedMapId);

const labelQueryOptions = (nestedMapId: string, search: SearchParams) => {
  return useLabel.getOptions({
    size: 20,
    page: search.p,
    filter: [
      {
        model: 'Label',
        field: 'Map',
        value: nestedMapId,
        op: 'eq',
      },
    ],
    sort: [
      {
        model: 'Label',
        field: 'Label',
        direction: 'asc',
      },
    ],
  });
};

/**
 * Route
 */
export const Route = createFileRoute('/beheer/labels/$mapId/$nestedMapId')({
  validateSearch: searchSchema.parse,
  beforeLoad: async ({ context, params }) => ({
    getTitle: () => {
      const { queryKey } = labelMapIdQueryOptions(params.nestedMapId);

      return context.queryClient.getQueryData<LabelMapSchema>(queryKey)?.Naam;
    },
  }),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({
    context: { queryClient },
    params: { mapId, nestedMapId },
    deps,
  }) => {
    const [labels, nestedMap] = await Promise.all([
      queryClient.ensureQueryData(labelQueryOptions(nestedMapId, deps.search)),
      queryClient.ensureQueryData(labelMapIdQueryOptions(nestedMapId)),
    ]);

    if (!nestedMap) throw notFound();
    if (!labels.num_pages) return;

    if (labels.num_pages < deps.search.p) {
      throw redirect({
        to: '/beheer/labels/$mapId/$nestedMapId',
        search: { p: labels.num_pages },
        params: { mapId: mapId, nestedMapId: nestedMapId },
      });
    }
  },
  component: LabelsComponent,
});

/**
 * Route component
 */
function LabelsComponent() {
  const { mapId, nestedMapId } = Route.useParams();

  const search = Route.useSearch();

  const map = useQuery(labelMapIdQueryOptions(nestedMapId));
  const labels = useQuery(labelQueryOptions(nestedMapId, search));

  if (!map.data || !labels.data) return null;

  return (
    <SidebarLayout>
      <Explorer
        backButton={
          <TextButton
            className="mb-0.5 flex items-center"
            variant="gray"
            size="small"
            asChild
          >
            <Link
              from={Route.fullPath}
              to="/beheer/labels/$mapId"
              search={{ p: 1 }}
            >
              <RegularChevronRight
                size={14}
                className="mr-1 inline-block rotate-180"
              />
              <span>Terug</span>
            </Link>
          </TextButton>
        }
        title={map.data.Naam}
      >
        <Explorer.ItemHeading
          title="Labels"
          button={
            <ModalTrigger
              modalComponent={Label}
              onOpen={() => ({ createLabel: { mapId: nestedMapId } })}
              asChild
            >
              <Button variant="secondary">Nieuw label</Button>
            </ModalTrigger>
          }
        />

        {!labels.data.objects?.length ? (
          <Explorer.Placeholder>
            Deze map heeft geen labels
          </Explorer.Placeholder>
        ) : (
          <>
            <ul>
              {labels.data.objects.map((label) => (
                <li key={`label-button-${label.ID}`}>
                  <ExplorerButtonLabel label={label} />
                </li>
              ))}
            </ul>

            {(labels.data?.num_pages ?? 0) > 1 ? (
              <div className="mt-5 flex w-full justify-center">
                <PaginationLinks
                  aria-label="Label pagina's"
                  renderLink={({ children, page }) => (
                    <Link
                      to="/beheer/labels/$mapId/$nestedMapId"
                      params={{ mapId, nestedMapId }}
                      search={{ p: page }}
                    >
                      {children}
                    </Link>
                  )}
                  currentPage={search.p}
                  totalPages={labels.data?.num_pages ?? 0}
                />
              </div>
            ) : null}
          </>
        )}
      </Explorer>
    </SidebarLayout>
  );
}
